import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalType } from '@context/ModalsContext'
import { useSnackbar } from '@context/SnackbarContext'

import { Input } from '@ui/components/Input'
import { Modal } from '@ui/components/Modal'

import { useUpdateDemoTradingAccountNickname } from '../hooks/useUpdateDemoTradingAccountNickname'
import { useUpdateTradingAccountNickname } from '../hooks/useUpdateTradingAccountNickname'

const MAX_LENGTH = 30

const filterLatinAlphanumeric = (value: string) => value.replace(/[^A-Za-z0-9 ]/g, '')

interface Props {
  tradingAccountId: string
  currentNickname?: string
  isDemo?: boolean
}

export const ChangeNicknameModal: BaseModalType<Props> = ({
  close,
  tradingAccountId,
  currentNickname = '',
  isDemo = false,
}) => {
  const { t } = useTranslation()
  const { addSnackbar } = useSnackbar()
  const [nickname, setNickname] = useState(currentNickname)
  const { mutate: updateLiveNickname, isPending: isLivePending } = useUpdateTradingAccountNickname()
  const { mutate: updateDemoNickname, isPending: isDemoPending } =
    useUpdateDemoTradingAccountNickname()
  const updateNickname = isDemo ? updateDemoNickname : updateLiveNickname
  const isPending = isDemo ? isDemoPending : isLivePending

  const isAtLimit = nickname.length >= MAX_LENGTH

  const handleConfirm = () => {
    updateNickname(
      { tradingAccountId, nickname: nickname || null },
      {
        onSuccess: () => {
          addSnackbar.success({ message: t('Trading Account.Nickname changed successfully') })
          close()
        },
        onError: () => {
          addSnackbar.error({ message: t('errors.Something went wrong') })
        },
      }
    )
  }

  return (
    <Modal mode='bottomSheet' size='sm'>
      <Modal.Header>{t('Trading Account.Change Nickname')}</Modal.Header>
      <Modal.Body>
        <Input
          size='xl'
          label={t('Trading Account.Nickname')}
          value={nickname}
          onChange={(e) => setNickname(filterLatinAlphanumeric(e.target.value))}
          maxLength={MAX_LENGTH}
          hint={`${nickname.length}/${MAX_LENGTH}`}
          error={isAtLimit}
          fullWidth
        />
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary disabled={isPending} onClick={close}>
          {t('Cancel')}
        </Modal.Footer.Secondary>
        <Modal.Footer.Primary loading={isPending} onClick={handleConfirm}>
          {t('Confirm')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}
